import { RefObject, useEffect, useState } from 'react';
import { ImageKit } from '@team/image';
import { Assert } from '@team/assert';
import {
  setupFadeOptions,
  setupGradientOptions,
  setupImageOptions,
} from './model';
import { Preset, ElementDimension, HookOptions } from './types';

/**
 * @todo при ресайзе фоновая картинка ресайзится быстрее градиента (не страшно)
 */
export function useFadedBackground(
  elementRef: RefObject<HTMLDivElement>,
  imagePath: ImageKit.ImagePath,
  {
    fadeAmount = 1,
    gradient,
    backgroundColor = 'var(--tgui--bg_color)',
  }: HookOptions = {}
) {
  const [elementDimension, setElementDimension] =
    useState<Nullable<ElementDimension>>(null);
  const [imageDimension, setImageDimension] =
    useState<Nullable<ElementDimension>>(null);

  useEffect(() => {
    if (Assert.isNull(elementRef.current)) {
      return;
    }

    const resizeObserver = new ResizeObserver(([entry]) => {
      setElementDimension({
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      });
    });

    resizeObserver.observe(elementRef.current);

    return () => {
      resizeObserver.disconnect();

      setElementDimension(null);
    };
  }, [elementRef]);

  useEffect(() => {
    let cleanup = false;

    void ImageKit.getOriginalDimension(imagePath)
      .then((result) => {
        if (cleanup) {
          return;
        }

        setImageDimension(result);
      })
      .catch((error: unknown) => {
        if (cleanup) {
          return;
        }

        if (import.meta.env.DEV) {
          console.warn(
            '[TelegramUi.useFadedBackground]: failed load image',
            error
          );
        }
      });

    return () => {
      cleanup = true;
    };
  }, [imagePath]);

  useEffect(() => {
    if (Assert.isNull(elementRef.current)) {
      return;
    }

    if (Assert.isNull(imageDimension) || Assert.isNull(elementDimension)) {
      return;
    }

    const element = elementRef.current;

    const imageHeight =
      (elementDimension.width * imageDimension.height) / imageDimension.width;

    const fadeOptions = setupFadeOptions(
      backgroundColor,
      imageHeight,
      fadeAmount
    );
    const imageOptions = setupImageOptions(imagePath);
    const gradientOptions = Assert.isString(gradient)
      ? setupGradientOptions(gradient, imageHeight)
      : null;

    const styles: Preset = {
      'background-attachment': 'scroll',
      'background-repeat': 'no-repeat',
      'background-image': [
        fadeOptions.image,
        imageOptions.image,
        gradientOptions?.image,
      ]
        .filter(Assert.isDefined)
        .join(),
      'background-position': [
        fadeOptions.position,
        imageOptions.position,
        gradientOptions?.position,
      ]
        .filter(Assert.isDefined)
        .join(),
      'background-size': [
        fadeOptions.size,
        imageOptions.size,
        gradientOptions?.size,
      ]
        .filter(Assert.isDefined)
        .join(),
    };

    const previousStyles: Preset = {
      'background-attachment': element.style.backgroundAttachment,
      'background-repeat': element.style.backgroundRepeat,
      'background-image': element.style.backgroundImage,
      'background-position': element.style.backgroundPosition,
      'background-size': element.style.backgroundSize,
    };

    Object.assign(element.style, styles);

    return () => {
      Object.assign(element.style, previousStyles);
    };
  }, [
    elementRef,
    elementDimension,
    imagePath,
    imageDimension,
    fadeAmount,
    gradient,
    backgroundColor,
  ]);
}
